import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import SectionHeading from "@/components/base/SectionHeading";
import { searchAll, getHotTvShows, getLatestMovies } from "@/lib/catalog";
import type { SearchResult } from "@/types/media";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);
  const [sort, setSort] = useState("relevance");

  useEffect(() => {
    setInput(query);
  }, [query]);

  useEffect(() => {
    document.title = query ? `搜索“${query}” - 麻豆网站` : "搜索 - 麻豆网站";
  }, [query]);

  useEffect(() => {
    setSort("relevance");
  }, [query]);

  const results = useMemo<SearchResult[]>(() => {
    const list = searchAll(query);
    if (sort === "date") {
      return [...list].sort((a, b) => b.date.localeCompare(a.date));
    }
    return [...list].sort((a, b) => b.order - a.order);
  }, [query, sort]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (q) setSearchParams({ q });
    else setSearchParams({});
  };

  const recommendations = [...getLatestMovies(3), ...getHotTvShows(3)].slice(0, 6);

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-12">
      <div className="max-w-3xl mx-auto text-center mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">全站搜索</h1>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影视名称或资讯关键词"
              className="w-full pl-11 pr-4 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </form>
      </div>

      {query && (
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <p className="text-sm text-foreground-600">
              关键词“<span className="font-semibold text-foreground-950">{query}</span>”共找到{" "}
              <span className="font-semibold text-secondary-500">{results.length}</span> 条结果
            </p>
            {results.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">排序</span>
                {[
                  { value: "relevance", label: "相关度" },
                  { value: "date", label: "更新时间" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSort(opt.value)}
                    className={`px-3 py-1 rounded-md text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      sort === opt.value
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200 rounded-lg mb-12">
              <i className="ri-search-eye-line text-5xl text-foreground-300 mb-4" />
              <p className="text-base font-medium text-foreground-700 mb-1">未找到相关内容</p>
              <p className="text-sm text-foreground-500">换个关键词试试，或看看下面的热门推荐</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 mb-12">
              {results.map((item) => (
                <Link
                  key={`${item.kind}-${item.id}`}
                  to={item.path}
                  className="group flex gap-4 p-3 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="relative w-20 h-20 md:w-24 md:h-24 flex-shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 麻豆网站`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                          item.kind === "media"
                            ? "bg-primary-100 text-primary-800"
                            : "bg-secondary-100 text-secondary-900"
                        }`}
                      >
                        {item.kind === "media" ? "影视" : "资讯"}
                      </span>
                      <span className="text-xs text-foreground-500 line-clamp-1">{item.meta}</span>
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-950 leading-snug line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs md:text-sm text-foreground-600 line-clamp-2">{item.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      <section className="max-w-5xl mx-auto">
        <SectionHeading
          eyebrow="Hot Picks"
          title={query && results.length === 0 ? "为你推荐热门内容" : "大家都在看"}
          description="近期人气最高的影视作品，点击卡片即可查看详情。"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {recommendations.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      </section>
    </div>
  );
}